import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaxPageHeader,
  FaxPageStyle,
  HeaderActions,
  Search,
} from "components/fax/SecureFax.style";

import {
  getFaxList,
  useInputListDetails,
} from "redux/slice/faxSlice";
import { useAppDispatch } from "redux/hooks";
import {
  setDeleteHideIn,
  setShowIncompleteFaxes,
  useHideDeleteIn,
  useInboxType,
  useShowIncompleteFaxes,
  useTab,
  useNumber,
} from "redux/slice/commanSlice";
import Table from "components/ui-component/Table";
import {
  Drawer,
  FloatButton,
  Pagination,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import SearchIcon from "components/Icons/SearchIcon";
import DownloadIcon from "components/Icons/Download";
import EditDropIcon from "components/Icons/EditDropIcon";
import DeleteThinIcon from "components/Icons/DeleteThinIcon";
import AddIconCollapseIcon from "components/Icons/AddIconCollapse";
import RefreshIcon from "components/Icons/Refresh";
import { Button } from "components/ui-component";
import TableCard, { FaxCardDataType } from "components/TableCard/TableCard";
import FilterIcon from "components/Icons/FilterIcon";
import SinglePlusIcon from "components/Icons/SinglePlusIcon";
import InboxFilters from "components/fax/InboxFilters";
import Lock from "components/Icons/Lock";
import ReadEyeIcon from "components/Icons/ReadEyeIcon";
import UnreadEyeIcon from "components/Icons/UnreadEyeSlash";
import { onDownloadFax } from "../Helper/DownloadFile";

interface InboxProps {
  filter: any;
  setFilter: any;
  setDeleteId: any;
  setSentFaxData: any;
  setIsWarn: any;
  isLoading: boolean;
  setFaxLogs: any;
}

const Inbox = (props: InboxProps) => {
  const {
    setDeleteId,
    setSentFaxData,
    setFilter,
    filter,
    isLoading,
  } = props;
  const { user } = useAuth0();
  const number = useNumber();
  const navigation = useNavigate();
  const inputFaxList = useInputListDetails();
  const dispatch = useAppDispatch();
  const inboxType = useInboxType();
  const activeTab = useTab();
  const hide_deletedIn = useHideDeleteIn();
  const showIncompleteFaxes = useShowIncompleteFaxes();

  const [filteredFaxes, setFilteredFaxes] = useState<FaxCardDataType[]>([]);
  const [openFilter, setOpenFilter] = useState(false);

  const showDrawer = () => {
    setOpenFilter(true);
  };

  const onClose = () => {
    setOpenFilter(false);
  };

  useEffect(() => {
    if (inputFaxList?.faxes) {
      // Client-side fallback: filter incomplete faxes until backend supports hide_incomplete param
      const filtered = showIncompleteFaxes
        ? inputFaxList.faxes
        : inputFaxList.faxes.filter((fax: any) => fax.status !== "incomplete");
      const formatedFiltered = filtered?.map((item: FaxCardDataType) => {
        return { ...item, key: item?.uuid };
      });
      setFilteredFaxes(formatedFiltered);
    }
  }, [showIncompleteFaxes, inputFaxList]);

  // Sync the incomplete toggle with the API filter (for when backend adds support)
  useEffect(() => {
    setFilter((prev: any) => ({
      ...prev,
      page: 1,
      hide_incomplete: showIncompleteFaxes ? "no" : "yes",
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIncompleteFaxes]);

  const columns = [
    {
      title: "From Number",
      dataIndex: "from_number",
      key: "from_number",
      render: (text: string, record: any) => (
        <div className="from-number-wrapper ">
          <Tooltip title={record?.viewed === "yes" ? "Read" : "Unread"}>
            <span className="font-14">
              {record?.viewed === "yes" ? <ReadEyeIcon /> : <UnreadEyeIcon />}
            </span>
          </Tooltip>
          <label>{text}</label>
          <Tooltip title="To ensure the utmost security and confidentiality, all faxes are individually encrypted using a Key Management Service">
            <span>
              <Lock className="lock" />
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "To Number",
      dataIndex: "to_number",
      key: "to_number",
    },
    {
      title: "Date",
      dataIndex: "created_on",
      key: "created_on",
    },
    {
      title: "Pages",
      dataIndex: "pages",
      key: "pages",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: "",
      key: "action",
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <div className="action-wrapper">
          <div className="securefax-action">
            {record?.status === "incomplete" && (
              <div className="securefax-action__list">
                <Tag color="warning">Incomplete</Tag>
              </div>
            )}
            {record?.download_link && (
              <Button
                type="text"
                className="securefax-action__list"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadFax(
                    record,
                    record?.download_link,
                    dispatch,
                    user,
                    navigation
                  );
                }}
                icon={
                  <Tooltip title="Download Fax">
                    <span>
                      <DownloadIcon />
                    </span>
                  </Tooltip>
                }
              />
            )}
            {record?.status !== "incomplete" && (
              <Button
                type="text"
                className="securefax-action__list"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(record?.uuid);
                }}
                icon={
                  <Tooltip title="Delete Fax">
                    <span>
                      <DeleteThinIcon width="14" height="14" strokeWidth="2" />
                    </span>
                  </Tooltip>
                }
              />
            )}
            <Button
              type="text"
              className="securefax-action__list"
              onClick={(e) => {
                e.stopPropagation();
                setSentFaxData(record);
              }}
              icon={
                <Tooltip title="Edit Fax">
                  <span>
                    <EditDropIcon />
                  </span>
                </Tooltip>
              }
            />
          </div>
        </div>
      ),
    },
  ];

  const handleChange = (e: any) => {
    const newHideDeleted = hide_deletedIn === "yes" ? "no" : "yes";
    setFilter({
      ...filter,
      page: 1,
      per_page: 10,
      hide_deleted: newHideDeleted,
    });
    dispatch(setDeleteHideIn(newHideDeleted));
  };

  // Poll every second to fetch updated inbound faxes as it's a requirement
  useEffect(() => {
    const fetchFaxList = async () => {
      try {
        const params = {
          ...filter,
          username: user?.email,
          phone_number: number,
          direction: "in",
        };
        await dispatch(getFaxList(params)).unwrap();
      } catch (error: any) {
        console.log("fax list get error -->", error?.message);
      }
    };
    const interval = setInterval(() => {
      fetchFaxList();
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, number]);

  return (
    <FaxPageStyle>
      <FaxPageHeader>
        <Typography.Title level={5}>{`Inbox (${
          inputFaxList?.total_faxes ?? 0
        })`}</Typography.Title>
        <Button
          type="text"
          className="refresh-icon"
          style={{ cursor: "pointer", paddingRight: "12px" }}
          onClick={() => setFilter({ ...filter })}
          icon={<RefreshIcon />}
        />
        <div className="mobile-hide tab-hide">
          <InboxFilters
            setFilter={setFilter}
            inboxType={inboxType}
            activeTab={activeTab}
            hide_deletedIn={hide_deletedIn}
            handleChange={handleChange}
            showIncompleteFaxes={showIncompleteFaxes}
            dispatch={dispatch}
            setShowIncompleteFaxes={setShowIncompleteFaxes}
          />
        </div>
        <Drawer
          title="Filter"
          closable={{ "aria-label": "Close Button" }}
          onClose={onClose}
          open={openFilter}
          className="filter-drawer"
        >
          <div className="d-flex flex-direction-columnu gap-y-4 align-items-start">
            <InboxFilters
              setFilter={setFilter}
              inboxType={inboxType}
              activeTab={activeTab}
              hide_deletedIn={hide_deletedIn}
              handleChange={handleChange}
              showIncompleteFaxes={showIncompleteFaxes}
              dispatch={dispatch}
              setShowIncompleteFaxes={setShowIncompleteFaxes}
              className="date-filter-wrapper"
            />
          </div>
        </Drawer>
        <HeaderActions>
          <Button
            type="text"
            className="filter-icon"
            onClick={showDrawer}
            icon={
              <Tooltip title="Filter">
                <span>
                  <FilterIcon />
                </span>
              </Tooltip>
            }
          />
          <div className="searchbar-wrapper">
            <Search
              prefix={<SearchIcon />}
              size="small"
              placeholder="Search by number or notes"
              inputMode="search"
              search={filter?.search_query}
              value={filter?.search_query ?? ""}
              onChange={(e: any) => {
                setFilter({
                  ...filter,
                  search_query: e.target.value,
                });
              }}
              maxLength={50}
            />
          </div>

          <div className="mobile-hide">
            <Button
              size="small"
              type="primary"
              onClick={() => {
                navigation("/fax/compose-fax");
              }}
              icon={<AddIconCollapseIcon width="20" height="20" />}
            >
              Compose a fax
            </Button>
          </div>
          <div className="desktop-hide">
            <FloatButton
              icon={<SinglePlusIcon />}
              onClick={() => navigation("/fax/compose-fax")}
            />
          </div>
        </HeaderActions>
      </FaxPageHeader>
      <div className="mobile-table-block desktop-hide">
        <TableCard
          faxCardData={filteredFaxes ?? []}
          onDownloadFax={(fax, downloadLink) =>
            onDownloadFax(fax, downloadLink, dispatch, user, navigation)
          }
          setSentFaxData={setSentFaxData}
          setDeleteId={setDeleteId}
          isLoading={isLoading}
          tabKeyName="inbox"
        />
        {!isLoading && (
          <div className="mobile-pagination">
            <Pagination
              size="small"
              total={inputFaxList?.total_faxes ?? 0}
              showSizeChanger={true}
              onShowSizeChange={(page, pageSize) => {
                setFilter({
                  ...filter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              pageSize={parseInt(filter?.per_page) ?? 10}
              current={filter?.page ?? 1}
              onChange={(page, pageSize) => {
                setFilter({
                  ...filter,
                  page: page,
                  per_page: pageSize,
                });
              }}
              showTotal={(total, range) => `${range[1]}/ ${total}`}
            />
          </div>
        )}
      </div>
      <div className="mobile-table-block mobile-hide w-100">
        <Table
          data={filteredFaxes ?? []}
          columns={columns}
          isLoading={isLoading}
          pagination={true}
          pageSize={parseInt(filter?.per_page) ?? 10}
          total={inputFaxList?.total_faxes ?? 0}
          currentPage={filter?.page ?? 1}
          handlePageChange={(pagination: any) => {
            setFilter({
              ...filter,
              page: pagination.current,
              per_page: pagination.pageSize,
            });
          }}
          onRowClick={(record: any) => {
            setSentFaxData(record)
          }}
          rowClassName="clickable-rows"
        />
      </div>
    </FaxPageStyle>
  );
};

export default Inbox;
